<template>
  <div
    class="vote-overlay"
    :class="{ 'is-submitting': props.isSubmitting }"
    :aria-busy="props.isSubmitting ? 'true' : 'false'"
    tabindex="0"
    @keydown="handleKeyDown"
  >
    <button
      class="close-btn"
      @click="handleCloseClick"
      :disabled="allowExit"
      aria-label="Close"
      title="Close"
    >
      <X />
    </button>
    <div
      v-if="props.showPreview"
      class="preview"
      aria-hidden="true"
    >
      <img
        v-if="props.previewSrc"
        :src="props.previewSrc"
        class="preview-img"
        alt=""
      />
      <BlurhashCanvas
        v-else-if="props.previewBlurhash"
        :hash="props.previewBlurhash"
        class="preview-img"
      />
      <SvgSpinner
        v-else-if="props.previewIsLoading"
        class="preview-spinner"
        aria-label="Loading preview…"
      />
    </div>
    <div
      class="overlayed-buttons"
    >
      <div
        class="up"
        :class="{ 'first-seen': !apiConfig.hasSeenVoteAnimation }"
        @click="emitIfInteractive('upvote')"
      >
        <SvgSymbol
          symbol="arrow_simplified_down"
          color="white"
        />
      </div>
      <div
        class="down"
        :class="{ 'first-seen': !apiConfig.hasSeenVoteAnimation }"
        @click="emitIfInteractive('downvote')"
      >
        <SvgSymbol
          symbol="arrow_simplified_down"
          color="white"
        />
      </div>
    </div>
    <div
      class="close-buttons"
    >
      <div
        class="close"
        :class="{ 'first-seen': !apiConfig.hasSeenVoteAnimation, disabled: props.isSubmitting }"
        @click="emitIfInteractive('close')"
      >
        <SvgSymbol
          symbol="close"
          color="white"
        />
      </div>
      <div
        class="save"
        :class="{ 'first-seen': !apiConfig.hasSeenVoteAnimation, pending: props.isSubmitting }"
        @click="emitIfInteractive('save')"
      >
        <SvgSpinner
          v-if="props.isSubmitting"
          :segments="props.spinnerSegments"
          :direction="props.spinnerDirection"
          aria-label="Submitting vote…"
        />
        <SvgSymbol
          v-else
          symbol="save"
          color="white"
        />
      </div>
    </div>
    <HydrusRating
      v-for="votableRating in votableRatings"
      :key="votableRating.service_key"
      :rating="votableRating"
      :show-buttons="false"
    />
    <div
      class="skip-buttons"
    >
      <div
        class="prev"
        :class="{ 'first-seen': !apiConfig.hasSeenVoteAnimation, disabled: props.isSubmitting }"
        @click="emitIfInteractive('prev')"
      >
        <SvgSymbol
          symbol="arrow_down"
          color="white"
        />
      </div>
      <div
        class="next"
        :class="{ 'first-seen': !apiConfig.hasSeenVoteAnimation, disabled: props.isSubmitting }"
        @click="emitIfInteractive('next')"
      >
        <SvgSymbol
          symbol="arrow_down"
          color="white"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from "vue";
import { X } from "lucide-vue-next";
import HydrusRating from "@/components/HydrusRating/index.vue";
import SvgSymbol from "@/components/Svg/SvgSymbol.vue";
import { useApiStore } from "@/stores/api";
import type { AnyRatingServiceIncludingValue } from "@/stores/ratingService";
import SvgSpinner, { type SpinnerDirection } from "@/components/Svg/SvgSpinner.vue";
import BlurhashCanvas from "@/components/BlurhashCanvas.vue";

const emit = defineEmits(["upvote", "downvote", "close", "save", "prev", "next"]);
const props = withDefaults(defineProps<{
  votableRatings: AnyRatingServiceIncludingValue[];
  isSubmitting?: boolean;
  spinnerSegments?: number;
  spinnerDirection?: SpinnerDirection;
  showPreview?: boolean;
  previewSrc?: string | null;
  previewBlurhash?: string | null;
  previewIsLoading?: boolean;
}>(), {
  isSubmitting: false,
  spinnerSegments: 12,
  spinnerDirection: "clockwise",
  showPreview: false,
  previewSrc: null,
  previewBlurhash: null,
  previewIsLoading: false,
});

const apiConfig = useApiStore();

const forbidSubmit = computed(() => props.isSubmitting && !apiConfig.allowExitWhileVoting);
const allowSubmit = computed(() => !forbidSubmit.value);

const shiftPressCount = ref(0);
let shiftPressTimeout: ReturnType<typeof setTimeout> | null = null;

onMounted(() => {
  const overlayEl = document.querySelector('.vote-overlay');
  if (overlayEl) {
    overlayEl.focus();
  }
});

onUnmounted(() => {
  if (shiftPressTimeout !== null) {
    clearTimeout(shiftPressTimeout);
  }
});

function emitIfInteractive(eventName: "upvote" | "downvote" | "close" | "save" | "prev" | "next") {
  if (props.isSubmitting) {
    return;
  }
  emit(eventName);
}

function handleCloseClick() {
  if (allowSubmit.value) {
    return;
  }
  emit("close");
}

function handleKeyDown(e: KeyboardEvent) {
  if (e.key === "Shift") {
    e.stopPropagation();
    shiftPressCount.value++;

    if (shiftPressTimeout !== null) {
      clearTimeout(shiftPressTimeout);
    }

    if (shiftPressCount.value >= 3) {
      // Force close on 3x Shift press
      emit("close");
      shiftPressCount.value = 0;
      return;
    }

    if (allowSubmit.value) {
      // Force close immediately if not locked
      emit("close");
      return;
    }
    // Reset counter after 500ms of no Shift press
    shiftPressTimeout = setTimeout(() => {
      shiftPressCount.value = 0;
    }, 500);
  }
}
</script>

<style scoped lang="scss">
$min_px_width_for_desktop_layout: 800px;

@keyframes showInitial {
  0% {
    opacity: 0.7;
    background: lawngreen;
  }
  5% {
    opacity: 1;
  }
  20% {
    opacity: 0.7;
  }
  25% {
    opacity: 1;
    background: lawngreen;
  }
  100% {
    background: transparent;
  }
}

@keyframes closeInitial {
  // wait 2s, but keep the opacity at 0 from the start.
  0% {
    opacity: 0;
  }
  // now do the actual animation
  // // To calculate the new percentage
  // // ((1/3)*2) + (0.50*(1/3))
  // // where 0.50 = 50%
  66% {
    // 0%
    opacity: 0;
    background: rgba(255, 0, 0, 0.7);
    border-color: rgba(255, 0, 0, 0.7);
  }
  68% {
    // 5%
    opacity: 1;
    background: rgba(255, 0, 0, 1);
    border-color: rgba(255, 0, 0, 1);
  }
  73% {
    // 20%
    background: rgba(255, 0, 0, 0.7);
    border-color: rgba(255, 0, 0, 0.7);
  }
  75% {
    // 25%
    background: rgba(255, 0, 0, 1);
    border-color: rgba(255, 0, 0, 1);
  }
  100% {
    // 100%
    background: transparent;
    border-color: transparent;
    opacity: 1;
  }
}

@keyframes saveInitial {
  // wait 2s, but keep the opacity at 0 from the start.
  0% {
    opacity: 0;
  }
  // now do the actual animation
  // // To calculate the new percentage
  // // ((1/3)*2) + (0.50*(1/3))
  // // where 0.50 = 50%
  66% {
    // 0%
    opacity: 0;
    background: rgba(124, 252, 0, 0.7);
    border-color: rgba(124, 252, 0, 0.7);
  }
  68% {
    // 5%
    opacity: 1;
    background: rgba(124, 252, 0, 1);
    border-color: rgba(124, 252, 0, 1);
  }
  73% {
    // 20%
    background: rgba(124, 252, 0, 0.7);
    border-color: rgba(124, 252, 0, 0.7);
  }
  75% {
    // 25%
    background: rgba(124, 252, 0, 1);
    border-color: rgba(124, 252, 0, 1);
  }
  100% {
    // 100%
    background: transparent;
    border-color: transparent;
    opacity: 1;
  }
}

@keyframes hideInitial {
  // wait until the end, then show
  0% {
    opacity: 0;
  }
  75% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.overlayed-buttons {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  z-index: 2;

  div {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: auto;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;

    svg {
      width: 5em;
      height: 5em;
    }

    &.up {
      &.first-seen {
        animation: 2s linear 0s 1 showInitial;
      }

      top: 0;
      bottom: 50%;

      svg {
        transform: rotate(180deg);
      }
    }

    &.down {
      &.first-seen {
        animation: 2s linear 1s 1 showInitial;
      }

      top: 50%;
      bottom: 0;
    }
  }
}

.preview {
  width: 6em;
  height: 6em;
  border-radius: var(--radius-md, 4px);
  overflow: hidden;
  flex-shrink: 0;
  margin-bottom: 0.5em;

  .preview-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .preview-spinner {
    width: 2em;
    height: 2em;
  }
}

.vote-overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-overlay);
  color: var(--text-primary);

  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  z-index: 15;
  outline: none;

  ::v-deep(svg) {
    width: 4em;
    height: 4em;
  }

  &.is-submitting {
    .up,
    .down,
    .close,
    .prev,
    .next {
      opacity: 0.45;
      cursor: default;
    }
  }

  .close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    font-size: 1.5rem;
    background: none;
    border: none;
    color: var(--text-primary);
    cursor: pointer;
    transition: color var(--transition-fast);
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 20;

    &:hover:not(:disabled) {
      color: var(--accent);
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }

    svg {
      width: 1.5em;
      height: 1.5em;
    }
  }

  .close-buttons {
    display: flex;
    flex-direction: row;

    .close,
    .save {
      z-index: 5;
      border-radius: 50%;

      border: 1.2em solid rgba(255, 0, 0, 0);
      svg {
        // A hack for the svg not being centered horizontally...
        margin-bottom: -0.3em;
      }
    }

    .close.first-seen {
      animation: 4s linear 0s 1 closeInitial;
    }

    .save.first-seen {
      animation: 4s linear 0s 1 saveInitial;
    }

    .save.pending {
      animation: none;
    }
  }
  .skip-buttons {
    display: flex;
    flex-direction: row;

    .prev, .next {
      z-index: 5;
      border-radius: 50%;

      border: 1.2em solid rgba(255, 0, 0, 0);

      svg {
        width: 2em;
        height: 2em;
      }

      &.first-seen {
        animation: 5s linear 0s 1 hideInitial;
      }
    }
    .prev {
      svg {
        transform: rotate(90deg);
      }
    }
    .next {
      svg {
        transform: rotate(-90deg);
      }
    }
  }
}
</style>
